// Single source of truth for "Registered address" vs "Yard entrance" deep links.
// Every directions / maps link in the site should go through these helpers so
// visitors who tap "directions" land at the gated yard, not down the wrong end
// of Pixley Street.

import { trackEvent } from "@/lib/tracking";

/** Verified yard-entrance coordinates (single source — keep in sync with admin compliance). */
export const ENTRANCE = {
  lat: 51.514512,
  lng: -0.029928,
  street: "14 Copenhagen Place",
  locality: "London",
  postcode: "E14 7DF",
  fullAddress: "14 Copenhagen Place, London E14 7DF",
} as const;

/** Verified registered address (Companies House / DVSA listing). */
export const REGISTERED = {
  street: "16 Pixley Street",
  locality: "London",
  postcode: "E14 7DF",
  fullAddress: "16 Pixley Street, London E14 7DF",
} as const;

export type DestinationKind = "entrance" | "registered";

/** Build a Google Maps directions URL with the chosen pin. */
export function buildDirectionsUrl(
  kind: DestinationKind,
  opts: { origin?: string; mode?: "driving" | "walking" | "transit" } = {},
): string {
  const params = new URLSearchParams({ api: "1" });
  if (kind === "entrance") {
    params.set("destination", `${ENTRANCE.lat},${ENTRANCE.lng}`);
  } else {
    params.set("destination", REGISTERED.fullAddress);
  }
  if (opts.mode) params.set("travelmode", opts.mode);
  if (opts.origin && opts.origin.trim()) params.set("origin", opts.origin.trim());
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Build a Google Maps "open place" URL (drops the pin without routing). */
export function buildMapsPlaceUrl(kind: DestinationKind): string {
  if (kind === "entrance") {
    return `https://www.google.com/maps/search/?api=1&query=${ENTRANCE.lat},${ENTRANCE.lng}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(REGISTERED.fullAddress)}`;
}

/** Apple Maps deep link — falls back gracefully on Android. */
export function buildAppleMapsUrl(kind: DestinationKind): string {
  if (kind === "entrance") {
    return `https://maps.apple.com/?ll=${ENTRANCE.lat},${ENTRANCE.lng}&q=${encodeURIComponent("Woodseer Autos — yard entrance")}`;
  }
  return `https://maps.apple.com/?q=${encodeURIComponent(REGISTERED.fullAddress)}`;
}

/**
 * Track a directions intent in a structured way so the admin dashboard can
 * break results down by source / kind / mode without inventing event names.
 */
export function trackDirectionsClick(opts: {
  source: string;
  kind: DestinationKind;
  mode?: "driving" | "walking" | "transit";
  origin_type?: "manual" | "geolocation" | "default";
  action?: string;
}): void {
  trackEvent("directions_click", {
    source: opts.source,
    destination: opts.kind,
    ...(opts.mode ? { mode: opts.mode } : {}),
    ...(opts.origin_type ? { origin_type: opts.origin_type } : {}),
    ...(opts.action ? { action: opts.action } : {}),
  });
}

/**
 * Build a WhatsApp link with prefilled written directions to the chosen pin —
 * for visitors who can't (or don't want to) use Google Maps. Optionally carries a
 * vehicle/booking note so the workshop can prep before they arrive.
 */
export function buildWhatsAppDirectionsUrl(
  routingNumber: string,
  source: string,
  opts: { kind?: DestinationKind; note?: string; origin?: string } = {},
): string {
  const number = (routingNumber || "442075372447").replace(/[^\d]/g, "");
  const kind: DestinationKind = opts.kind || "entrance";
  const isEntrance = kind === "entrance";
  const lines: string[] = [];
  if (isEntrance) {
    lines.push("Hi Woodseer Autos — please send me directions to the workshop YARD ENTRANCE.");
    lines.push("");
    lines.push(`📍 ${ENTRANCE.fullAddress}`);
    lines.push(`🧭 Coordinates: ${ENTRANCE.lat}, ${ENTRANCE.lng}`);
    lines.push(`(Registered office: ${REGISTERED.fullAddress})`);
  } else {
    lines.push("Hi Woodseer Autos — please send me directions to your REGISTERED address.");
    lines.push("");
    lines.push(`📍 ${REGISTERED.fullAddress}`);
    lines.push(`(Yard entrance is round the corner at ${ENTRANCE.street}, ${ENTRANCE.postcode})`);
  }
  lines.push("");
  if (opts.origin && opts.origin.trim()) {
    lines.push(`I'm coming from: ${opts.origin.trim()}`);
  } else {
    lines.push("I'm coming from: ");
  }
  if (opts.note && opts.note.trim()) {
    lines.push("");
    lines.push(`Booking note: ${opts.note.trim()}`);
  }
  const message = lines.join("\n");
  const url = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
  // Annotate the URL so click handlers can pass `source` for attribution.
  return url + `#src=${encodeURIComponent(source)}`;
}
